import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

const PriceInfoToolTip = ({
  isLoggedIn,
  cheapestPrice,
  handleLoyaltySignUpClick,
  eventObj,
  couponShowHideInfoTooltipFunc,
  pharmacyName,
  isFromCouponPage = false
}) => {
  const { t, i18n } = useTranslation();
  const wrapperRef = useRef();
  const { LoyaltyPrice, Price, LoyaltyBonusSavings, LoyaltyDiscounts } =
    cheapestPrice;
  const [isPriceTooltipClick, setIsPriceTooltipClick] = useState(false);
  const [isHoverActive, setIsHoverActive] = useState(false);
  const [topScroll, setTopScroll] = useState(false);

  const handleClickOutsideCouponInfo = event => {
    if (
      wrapperRef &&
      !wrapperRef.current.contains(event.target) &&
      !event.target.className.includes('infoImage')
    ) {
      setIsPriceTooltipClick(false);
    }
  };

  const hideCouponToolTip = () => {
    setIsPriceTooltipClick(false);
  };

  useEffect(() => {
    couponShowHideInfoTooltipFunc.current = hideCouponToolTip;
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideCouponInfo);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideCouponInfo);
    };
  });

  const handleInfoIconToolTip = () => {
    setIsPriceTooltipClick(!isPriceTooltipClick);
    isPriceTooltipClick && infoIconDataLayerEvent();
  };

  const infoIconDataLayerEvent = () => {
    _.triggerGTMVirtualPageViewEvent({
      event: 'yourPriceTooltipOpen',
      ...eventObj
    });
  };

  let discountAmount =
    !_.isEmpty(LoyaltyDiscounts) && LoyaltyDiscounts[0].discountAmount;

  const isLoyaltyBonusSavingsEmpty =
    LoyaltyBonusSavings === '0.00' || LoyaltyBonusSavings === '';

  const isLoyaltyDiscountBonus =
    discountAmount != '0.00' || !isLoyaltyBonusSavingsEmpty;

  const numberWithCommas = (price = '') => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formattedPrice = (price = '') => {
    return Number(price) > 1000
      ? numberWithCommas(_.round(price))
      : Number(price).toFixed(2);
  };

  const totalPriceDiv = () => {
    return (
      <p className="pharmacy-item__price pricingnewInfo">
        <b>$</b>
        {formattedPrice(LoyaltyPrice)}
      </p>
    );
  };

  const singleCarePriceComponent = () => {
    return (
      <li className="priceDetails">
        <div className="priceDesc">
          {t('Price if you don’t')} {t('sign-up-info')}:
        </div>
        <div className="totalPrice">${formattedPrice(Price)}</div>
      </li>
    );
  };

  const singleCareMemberPriceComponent = () => {
    return (
      <li className="priceDetails">
        <div className="priceDesc">{t('Member price')}:</div>
        {!discountAmount ? (
          <div className="totalPrice">${formattedPrice(Price)}</div>
        ) : (
          <div className="totalPrice">
            ${formattedPrice(_.round(Price - discountAmount, 2))}
          </div>
        )}
      </li>
    );
  };

  const memberComponent = () => {
    return (
      <li className="priceDetails">
        <div className="priceDesc">{t('Member discount')}:</div>
        <div className="totalPrice">-${formattedPrice(discountAmount)}</div>
      </li>
    );
  };

  const bonusComponent = () => {
    return (
      <li className={`priceDetails ${isLoggedIn && 'bonusPriceText'}`}>
        {!isLoggedIn ? (
          <div className="priceDesc">{t('One-time bonus')}:</div>
        ) : (
          <div className="priceDesc">{t('Bonus savings balance')}:</div>
        )}
        <div className="totalPrice">-${LoyaltyBonusSavings}</div>
      </li>
    );
  };

  const yourPriceComponent = () => {
    return (
      <li className="priceDetails totalDetail">
        {isLoggedIn ? (
          <div className="priceDesc">{t('Your price')}:</div>
        ) : i18n.language === 'en' ? (
          <div className="priceDesc">Price with free signup:</div>
        ) : (
          <div className="priceDesc">Precio con registro gratuito:</div>
        )}
        <div className="totalPrice mainPrice">
          = ${formattedPrice(LoyaltyPrice)}
        </div>
      </li>
    );
  };

  const infoIcon = () => {
    return (
      <button
        className={`dialogComponent infoIcon ${
          isLoggedIn ? 'loginInfoIcon' : ''
        }`}
        aria-label={t('How is this calculated')}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleInfoIconToolTip();
          }
        }}
        aria-describedby={`${_.createIDFromPharmacyName(
          pharmacyName
        )}-coupon-info-tooltip`}
        onMouseLeave={() => setIsHoverActive(false)}
      >
        <div
          onMouseEnter={() => {
            infoIconDataLayerEvent();
            setIsHoverActive(true);
          }}
          onClick={() => {
            handleInfoIconToolTip();
          }}
          role="tooltip"
          className={`infoImage ${
            (isPriceTooltipClick || isHoverActive) && 'active'
          }`}
          {...(isFromCouponPage && {
            onMouseOver: e => {
              setTopScroll(
                _.getIsMobileDevice() ? e.clientY < 230 : e.clientY < 271
              );
            }
          })}
          aria-label={t('Open pricing info')}
        ></div>
      </button>
    );
  };

  const getSignupSpanProps = () => {
    return {
      onKeyDown: e => {
        if (e.key === 'Enter') {
          handleLoyaltySignUpClick();
        }
      },
      onClick: () => handleLoyaltySignUpClick(),
      className: 'dialogComponent',
      'aria-label': t('sign-up (opens dialog)'),
      role: 'button'
    };
  };

  return (
    <div className="PricingWrapInfo">
      {!isLoyaltyDiscountBonus && isFromCouponPage && (
        <p className="noOfferPrice">Your price:</p>
      )}
      {!isLoggedIn && totalPriceDiv()}
      <div className="PriceSectionInfo">
        <div
          className={`pricetoolTipInfo ${
            isFromCouponPage && isLoggedIn ? 'loginPricetoolTipInfo' : ''
          } ${
            isFromCouponPage && !isLoyaltyDiscountBonus
              ? 'noOfferPricetoolTipInfo'
              : ''
          }`}
        >
          {isLoyaltyDiscountBonus && infoIcon()}

          <ul
            role="status"
            id={`${_.createIDFromPharmacyName(
              pharmacyName
            )}-coupon-info-tooltip`}
            ref={wrapperRef}
            className={`visually-hidden ${
              isPriceTooltipClick || isHoverActive ? 'visually-visible' : ''
            } ${!isLoggedIn ? 'nonLoginTooltip' : ''} ${
              topScroll && isFromCouponPage ? 'hoverSelected' : ''
            }`}
            onMouseEnter={() => setIsHoverActive(true)}
            onMouseLeave={() => setIsHoverActive(false)}
          >
            <li>
              <div className="tooltipHeading">
                {t('How is this calculated')}
              </div>
            </li>
            {isLoggedIn
              ? singleCareMemberPriceComponent()
              : singleCarePriceComponent()}
            {!isLoggedIn && discountAmount != '0.00' && memberComponent()}
            {!isLoyaltyBonusSavingsEmpty && bonusComponent()}
            {yourPriceComponent()}
            {!isLoggedIn && (
              <li>
                <p className="bonusNote">{t('Sign up description')}</p>
              </li>
            )}
            {isLoggedIn && (
              <li>
                <p className="savingNote">
                  {t('Bonus savings apply automatically')}
                </p>
              </li>
            )}
            {!isLoggedIn && (
              <li
                {...getSignupSpanProps()}
                tabIndex={isPriceTooltipClick ? '0' : '-1'}
                className="signUpButton dialogComponent"
              >
                {t('Sign up for free price')}
              </li>
            )}
          </ul>

          {!isLoggedIn && isLoyaltyDiscountBonus && (
            <p className="priceWrap withMemberBonus">
              <span {...getSignupSpanProps()} tabIndex="0">
                {t('With Member Bonus')}
              </span>
            </p>
          )}

          {isLoggedIn && isLoyaltyDiscountBonus && (
            <p className="yourPrice">{t('Your price')}</p>
          )}
        </div>

        {!isLoggedIn && isLoyaltyDiscountBonus && (
          <p className="withoutsignupLabel">
            {t('Without Member Bonus')}: <label>${formattedPrice(Price)}</label>
          </p>
        )}
      </div>
      {isLoggedIn && totalPriceDiv()}
      {isFromCouponPage && isLoggedIn && isLoyaltyDiscountBonus && (
        <div className="earnText">Earn $1 when you fill this script</div>
      )}
    </div>
  );
};
export default PriceInfoToolTip;
